import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ArrowLeft, Search, Download, Users, Calendar, MapPin, Plane, Bus, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPassengers = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [manualReservations, setManualReservations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtros
  const [filterPackage, setFilterPackage] = useState('all');
  const [filterDate, setFilterDate] = useState('all');

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resResponse, manualResponse, pkgResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reservations/`),
        axios.get(`${BACKEND_URL}/api/manual-reservations/`),
        axios.get(`${BACKEND_URL}/api/packages/?active_only=false`)
      ]);
      setReservations(resResponse.data);
      setManualReservations(manualResponse.data);
      setPackages(pkgResponse.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Combinar reservas do site + manuais
  const allReservations = useMemo(() => {
    const siteRes = reservations.map(r => ({ ...r, source: 'site' }));
    const manualRes = manualReservations.map(r => ({ ...r, source: 'manual' }));
    return [...siteRes, ...manualRes];
  }, [reservations, manualReservations]);

  // Extrair todas as datas únicas baseadas no pacote selecionado
  const uniqueDates = useMemo(() => {
    const dates = new Set();
    let filteredRes = allReservations;
    
    // Se um pacote está selecionado, filtrar datas apenas desse pacote
    if (filterPackage !== 'all') {
      filteredRes = allReservations.filter(r => r.package_id === filterPackage);
    }
    
    filteredRes.forEach(res => {
      if (res.selected_date) dates.add(res.selected_date);
    });
    return Array.from(dates).sort();
  }, [allReservations, filterPackage]);

  // Extrair todos os pacotes únicos das reservas
  const uniquePackages = useMemo(() => {
    const pkgs = new Map();
    allReservations.forEach(res => {
      if (res.package_id && res.package_name) {
        pkgs.set(res.package_id, res.package_name);
      }
    });
    return Array.from(pkgs.entries());
  }, [allReservations]);

  // Extrair todos os passageiros das reservas filtradas
  const passengers = useMemo(() => {
    let filteredReservations = allReservations;

    // Filtrar por pacote
    if (filterPackage !== 'all') {
      filteredReservations = filteredReservations.filter(r => r.package_id === filterPackage);
    }

    // Filtrar por data
    if (filterDate !== 'all') {
      filteredReservations = filteredReservations.filter(r => r.selected_date === filterDate);
    }

    // Extrair passageiros
    const allPassengers = [];
    filteredReservations.forEach(res => {
      // Status válidos: pago_total, pago_parcial, confirmed, pending, paid, approved
      const validStatuses = ['pago_total', 'pago_parcial', 'confirmed', 'pending', 'atrasado', 'paid', 'approved'];
      if (!validStatuses.includes(res.status) && !validStatuses.includes(res.payment_status)) return;
      
      // Ignorar cancelados
      if (res.status === 'cancelado' || res.status === 'cancelled') return;

      if (res.travelers && res.travelers.length > 0) {
        res.travelers.forEach((traveler, idx) => {
          // Normalizar campo de data de nascimento (pode vir como birthdate ou birth_date)
          const birthDate = traveler.birthdate || traveler.birth_date || '-';
          allPassengers.push({
            ...traveler,
            name: (traveler.name || '').toString().toUpperCase(),
            birth_date: birthDate,
            rg: traveler.rg || (traveler.document_type === 'rg' ? traveler.document_number : '') || '',
            passport: traveler.passport || (traveler.document_type === 'passaporte' ? traveler.document_number : '') || '',
            reservation_id: res.id,
            package_name: res.package_name,
            package_category: res.package_category,
            selected_date: res.selected_date,
            boarding_point: res.boarding_point || res.city_departure || '-',
            room_type: traveler.room_type || res.room_type,
            status: res.status || res.payment_status,
            source: res.source || 'site'
          });
        });
      } else {
        // Reservas antigas sem array de viajantes
        allPassengers.push({
          name: (res.customer_name || '').toString().toUpperCase(),
          cpf: res.customer_cpf,
          birth_date: res.customer_birthdate || res.birthdate || '-',
          rg: res.customer_rg || '',
          passport: res.customer_passport || '',
          email: res.customer_email,
          phone: res.customer_phone,
          reservation_id: res.id,
          package_name: res.package_name,
          package_category: res.package_category,
          selected_date: res.selected_date,
          boarding_point: res.boarding_point || res.city_departure || '-',
          room_type: res.room_type,
          status: res.status || res.payment_status,
          is_main: true,
          source: res.source || 'site'
        });
      }
    });

    // Filtrar por termo de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return allPassengers.filter(p => 
        p.name?.toLowerCase().includes(term) ||
        p.cpf?.includes(term)
      );
    }

    return allPassengers;
  }, [reservations, filterPackage, filterDate, searchTerm]);

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  };

  const isAirTravel = (category) => {
    if (!category) return false;
    return category.includes('aereo') || category === 'internacional';
  };

  const exportToCSV = () => {
    const headers = ['Nome Completo', 'CPF', 'RG', 'Passaporte', 'Data de Nascimento', 'Origem', 'Ponto de Embarque', 'Pacote', 'Data da Viagem', 'Status'];
    const rows = passengers.map(p => [
      p.name || '',
      formatCPF(p.cpf),
      p.rg || '',
      p.passport || '',
      p.birth_date || '',
      p.source === 'manual' ? 'Manual' : 'Site',
      p.boarding_point || '',
      p.package_name || '',
      p.selected_date || '',
      p.status || ''
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    
    const packageName = filterPackage !== 'all' 
      ? uniquePackages.find(p => p[0] === filterPackage)?.[1] || 'todos'
      : 'todos';
    const dateName = filterDate !== 'all' ? filterDate.replace(/\//g, '-') : 'todas';
    
    link.download = `passageiros_${packageName}_${dateName}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <Users className="w-6 h-6" />
            <h1 className="text-xl font-bold">Lista de Passageiros</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadData} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={exportToCSV} className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filtros */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Busca */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome ou CPF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filtro por Pacote */}
              <Select value={filterPackage} onValueChange={setFilterPackage}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por pacote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os pacotes</SelectItem>
                  {uniquePackages.map(([id, name]) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Filtro por Data */}
              <Select value={filterDate} onValueChange={setFilterDate}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por data" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as datas</SelectItem>
                  {uniqueDates.map(date => (
                    <SelectItem key={date} value={date}>{date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Contador */}
              <div className="flex items-center justify-center bg-[#003366] text-white rounded-lg px-4">
                <Users className="w-5 h-5 mr-2" />
                <span className="font-bold">{passengers.length}</span>
                <span className="ml-1 text-sm">passageiro(s)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabela de Passageiros */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Nome Completo</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">CPF</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">RG</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Passaporte</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Data Nasc.</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Origem</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Embarque</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Pacote</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Data Viagem</th>
                  </tr>
                </thead>
                <tbody>
                  {passengers.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>Nenhum passageiro encontrado</p>
                        <p className="text-sm">Ajuste os filtros ou aguarde novas reservas</p>
                      </td>
                    </tr>
                  ) : (
                    passengers.map((passenger, index) => (
                      <tr key={`${passenger.reservation_id}-${index}`} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-500">{index + 1}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {passenger.is_main && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Resp.</span>
                            )}
                            <span className="font-medium uppercase">{passenger.name || '-'}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm font-mono">{formatCPF(passenger.cpf)}</td>
                        <td className="p-3 text-sm">{passenger.rg || '-'}</td>
                        <td className="p-3 text-sm">{passenger.passport || '-'}</td>
                        <td className="p-3 text-sm">{passenger.birth_date || '-'}</td>
                        <td className="p-3">
                          {passenger.source === 'manual' ? (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                              Manual
                            </span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                              Site
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-sm">
                            {isAirTravel(passenger.package_category) ? (
                              <Plane className="w-3 h-3 text-blue-500" />
                            ) : (
                              <MapPin className="w-3 h-3 text-green-500" />
                            )}
                            {passenger.boarding_point}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-sm">
                            {isAirTravel(passenger.package_category) ? (
                              <Plane className="w-3 h-3 text-blue-500" />
                            ) : (
                              <Bus className="w-3 h-3 text-green-500" />
                            )}
                            <span className="max-w-[150px] truncate">{passenger.package_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-sm">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            {passenger.selected_date}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPassengers;
